<template lang="pug">
v-container.align-self-start(fluid :class="$vuetify.breakpoint.lgAndUp ? 'mw' : ''")
    v-snackbar(v-model="showSnackBar" :timeout="3000" :color="snackBarColor" )
      span {{ snackBarMessage }}
        v-btn(fab text @click="showSnackBar = false")
          v-icon fal fa-times-circle
    template(v-if="invoice")
        v-row.ma-0
          v-col.px-0.pb-6(cols="4")
            div {{ $t('paymentPage.invoiceName') }}:
          v-col.px-0.pb-6.text-end(cols="8")
            div {{ cardLabel }}

        v-divider

        v-row.ma-0
            v-col.pa-0(cols="12")
                v-row
                    v-col(cols="7")
                        div {{ $t('paymentPage.priceForFood') }}:
                    v-col.text-right(cols="5")
                        v-row.pr-4
                            v-spacer
                            div {{sumPrice}}
                            v-icon.ml-1(small color="black") fal fa-euro-sign
                v-row.red--text(v-if="invoice.amountFromDept > 0")
                    v-col(cols="7")
                        div {{ $t('paymentPage.unpaidPayment') }}:
                    v-col.text-right(cols="5")
                        v-row.pr-4
                            v-spacer
                            div {{amountFromDept}}
                            v-icon.ml-1(small color="red") fal fa-euro-sign
                v-row.green--text(v-if="invoice.amountFromWallet > 0")
                    v-col(cols="7")
                        div {{ $t('paymentPage.paidFromCredit') }}:
                    v-col.text-right(cols="5")
                        v-row.pr-4
                            v-spacer
                            div {{amountFromWallet}}
                            v-icon.ml-1(small color="green") fal fa-euro-sign
                v-row
                    v-col(cols="12")
                        v-divider
                v-row
                    v-col(cols="7")
                        div.text-h6.font-weight-bold {{ $t('paymentPage.toPaid') }}:
                    v-col.text-right(cols="5")
                        v-row.pr-4
                            v-spacer
                            div.text-h6.font-weight-bold {{totalPrice}}
                            v-icon.ml-1.font-weight-bold(small) fal fa-euro-sign
                v-row
                    v-col
                        v-divider

                v-container.pa-0(v-if="invoice.priceTotal > 0" fluid)
                    v-row
                        v-col(cols="12")
                            v-list(outlined)
                                v-list-item
                                    v-list-item-content
                                        v-list-item-subtitle {{ $t('paymentPage.ibanLabel') }}
                                        v-list-item-title.pt-1.font-weight-bold.text-h6.text-wrap {{invoice.iban}}
                                v-list-item
                                    v-list-item-content
                                        v-list-item-subtitle {{ $t('paymentPage.variableSymbol') }}
                                        v-list-item-title.pt-1.font-weight-bold.text-h6.text-wrap {{invoice.boarderBankIdentificationCode}}
                                v-list-item
                                    v-list-item-content
                                        v-list-item-subtitle {{ $t('paymentPage.specificSymbol') }}
                                        v-list-item-title.pt-1.font-weight-bold.text-h6.text-wrap {{invoice.specificSymbol}}
                                v-list-item
                                    v-list-item-content
                                        v-list-item-subtitle {{ $t('paymentPage.constantSymbol') }}
                                        v-list-item-title.pt-1.text-wrap {{invoice.constantSymbol}}
                    v-row
                        v-card(flat)
                            v-card-text
                                span(v-html=" $t('paymentPage.warningMessage') " )

                v-container.pa-0(fluid)
                    v-row
                        v-card(flat)
                            v-card-text
                                span {{ $t('paymentPage.infoMessage') }}
                            v-card-actions.justify-center
                                v-btn(text color="primary" small :loading="downloadInProgress" @click.prevent="onDownloadInvoiceButtonClick")
                                    v-icon fal fa-file-pdf
                                    span.ml-2 {{ $t('paymentPage.downloadInvoice') }}


        // QR kod zablokovany dokial sa nevyriesia problemy s generovanim PBS kodu na BE
        // v-col.pt-3.justify-center(cols="12" sm="12" md="6" lg="4")
            PayBySquare(:paymentCode="invoiceData.payBySquareCode").box
    v-skeleton-loader(v-else type="list-item-three-line@7")


</template>

<script lang="ts">
import Vue from 'vue'
import {Component} from 'vue-property-decorator'
import _ from 'lodash'
import {Action} from 'vuex-class'
import {DOWNLOAD_INVOICE, FETCH_MY_LATEST_INVOICES} from '@/store/action-types'
import {InvoiceResponseItem, MyLatestInvoiceResponse} from '@/types/service-response'
import Moment from "moment";
import VueI18n from "vue-i18n";
import TranslateResult = VueI18n.TranslateResult;

@Component({
  components: {
    // PayBySquare
  }
})
export default class PaymentPage extends Vue {
  private invoice: InvoiceResponseItem | null = null
  private downloadInProgress: boolean = false
  private showSnackBar: boolean = false
  private snackBarMessage: TranslateResult | string = ''
  private snackBarColor: string = ''

  @Action(FETCH_MY_LATEST_INVOICES) fetchInvoices!: ({ (): Promise<MyLatestInvoiceResponse> })
  @Action(DOWNLOAD_INVOICE) downloadInvoice!: ({ (invoice: InvoiceResponseItem): Promise<void> })

  get sumPrice(): string {
    return this.formatPrice(this.invoice!.priceItemSum)
  }

  get amountFromWallet(): string {
    return this.formatPrice(this.invoice!.amountFromWallet)
  }

  get amountFromDept(): string {
    return this.formatPrice(this.invoice!.amountFromDept)
  }

  get totalPrice(): string {
    return this.formatPrice(this.invoice!.priceTotal)
  }

  get cardLabel(): string {

    return Moment(this.invoice!.periodStart).format('D.M') + ' ' + '-' + ' ' + Moment(this.invoice!.periodEnd)
      .locale('sk-sk')
      .format('DD.MM.YYYY')
  }

  async onDownloadInvoiceButtonClick(): Promise<void> {
    if (this.invoice) {
      this.downloadInProgress = true

      try {
        await this.downloadInvoice(this.invoice)
      } catch (e) {
        this.snackBarColor = 'error'
        this.snackBarMessage = this.$t('paymentPage.errorMessage')
        this.showSnackBar = true
      }
      finally {
        this.downloadInProgress = false
      }
    }
  }

  formatPrice(price: number): string {
    const formatter = new Intl.NumberFormat('sk-SK', {
      style: 'decimal',
      minimumFractionDigits: 2
    })

    return formatter.format(price / 100)
  }

  async fetchData(): Promise<void> {
    this.invoice = _.first(await this.fetchInvoices()) || null
  }

  mounted() {
    this.fetchData()
  }
}

</script>

<style lang="stylus" scoped>
.mw
  max-width 600px

</style>
